import React, { useState, useEffect, useContext } from "react";
import { BarChart2, Users, BookOpen, Mail, Calendar, Archive, Plus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend }
from "recharts";
import { ApiUrlContext } from "../App";
import AdminAppointmentPanel from "./AdminAppointmentPanel";
import InventoryManagement from "./InventoryManagement";

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({ 
    totalUsers: 0, 
    totalCourses: 0, 
    totalEnrollments: 0 
  });
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Contact management states
  const [contacts, setContacts] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [contactStats, setContactStats] = useState({
    total: 0,
    byStatus: {
      pending: 0,
      reviewed: 0,
      contacted: 0,
      processed: 0,
      rejected: 0
    },
    last7Days: []
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactFilters, setContactFilters] = useState({
    status: '',
    search: ''
  });
  const [contactPagination, setContactPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  
  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    instructor: "",
    instructorEmail: "",
    duration: "",
    level: "Beginner",
    price: ""
  });
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const apiUrl = useContext(ApiUrlContext);

  // Tabs array'ine "contacts" tab'ını ekleyin:
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: <BarChart2 /> },
    { id: "users", label: "Kullanıcılar", icon: <Users /> },
    { id: "courses", label: "Kurslar", icon: <BookOpen /> },
    { id: "contacts", label: "İletişim Formları", icon: <Mail /> },
    { id: "appointments", label: "Randevular", icon: <Calendar /> },
    { id: "inventory", label: "Envanter", icon: <Archive /> },
    { id: "create-course", label: "Kurs Ekle", icon: <Plus /> }
  ];

  // Animations için global styles
  const globalStyles = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
// Arama inputu için debounce mekanizması
useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(contactFilters.search);
  }, 500);
  return () => clearTimeout(timer);
}, [contactFilters.search]);

 useEffect(() => {
    // KORUMA: fetchAdminData fonksiyonunu sadece dashboard sekmesi açıkken çağır
    if (activeTab === "dashboard") {
      fetchAdminData();
    }
    
    // KORUMA: Form listesini ve istatistiklerini sadece İletişim Formları sekmesi açıkken çağır
    if (activeTab === 'contacts') {
      fetchContacts();
      fetchContactStats();
    }
  }, [activeTab, contactFilters.status, debouncedSearch, contactPagination.page]);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, coursesRes, statsRes, instructorsRes] = await Promise.all([
        fetch(`${apiUrl}/admin/users`, { headers }),
        fetch(`${apiUrl}/admin/courses`, { headers }),
        fetch(`${apiUrl}/admin/stats`, { headers }),
        fetch(`${apiUrl}/admin/instructors`, { headers })
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (coursesRes.ok) setCourses(await coursesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (instructorsRes.ok) setInstructors(await instructorsRes.json());

    } catch (error) {
      console.error("Admin verisi yüklenirken hata:", error);
      showNotification("Veri yüklenirken hata oluştu", "error");
    } finally {
      setLoading(false);
    }
  };

 const fetchContacts = async () => {
  setContactLoading(true);
  try {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams({
  status: contactFilters.status,
  search: debouncedSearch,
  page: contactPagination.page,
  limit: contactPagination.limit
});
    

    const response = await fetch(`${apiUrl}/contact/admin/list?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setContacts(data.contacts);
        setContactPagination(prev => ({
          ...prev,
          total: data.total,
          totalPages: data.totalPages
        }));
      } else {
        setContacts([]);
      }
    } else {
      setContacts([]);
    }
  } catch (error) {
    console.error("Contact listesi yüklenirken hata:", error);
    showNotification("Formlar yüklenirken hata oluştu", "error");
    setContacts([]);
  } finally {
    setContactLoading(false);
  }
};

  const fetchContactStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/contact/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContactStats(data.stats);
        }
      }
    } catch (error) {
      console.error("Contact istatistikleri yüklenirken hata:", error);
    }
  };

  const handleContactStatusChange = async (contactId, newStatus) => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${apiUrl}/contact/admin/${contactId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ status: newStatus })
    });
    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Güncellenen contact'ı contacts dizisinde güncelle
        setContacts(prev =>
          prev.map(contact =>
            contact._id === contactId
              ? { ...contact, status: newStatus }
              : contact
          )
        );
        // Eğer detay modal açıksa, oradaki contact'ı da güncelle
        if (selectedContact && selectedContact._id === contactId) {
          setSelectedContact(prev => ({ ...prev, status: newStatus }));
        }
        showNotification("Durum güncellendi");
      }
    }
  } catch (error) {
    console.error("Contact durumu güncellenirken hata:", error);
    showNotification("Durum güncellenirken hata oluştu", "error");
  }
};

  const handleViewContactDetails = async (contactId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/contact/admin/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedContact(data.contact);
          setShowContactModal(true);
        }
      }
    } catch (error) {
      console.error("Contact detayları yüklenirken hata:", error);
      showNotification("Detaylar yüklenirken hata oluştu", "error");
    }
  };
  const handleUserRoleChange = async (userId, newRole) => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${apiUrl}/admin/users/${userId}/role`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ role: newRole })
    });
    if (response.ok) {
      showNotification("Kullanıcı rolü güncellendi");
      setUsers(prev =>
        prev.map(user =>
          user._id === userId ? { ...user, role: newRole } : user
        )
      );
    }
  } catch (error) {
    showNotification("Rol güncellenirken hata oluştu", "error");
  }
};

  const handleUpdateAdminNotes = async (contactId, notes) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/contact/admin/${contactId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ adminNotes: notes })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedContact(data.contact);
          showNotification("Notlar güncellendi");
        }
      }
    } catch (error) {
      console.error("Admin notları güncellenirken hata:", error);
      showNotification("Notlar güncellenirken hata oluştu", "error");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Bu formu silmek istediğinizden emin misiniz?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/contact/admin/${contactId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContacts(prev => prev.filter(contact => contact._id !== contactId));
          fetchContactStats();
          showNotification("Form başarıyla silindi");
        }
      }
    } catch (error) {
      console.error("Contact silinirken hata:", error);
      showNotification("Form silinirken hata oluştu", "error");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };
const handleAssignCourse = async (userId, courseId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${apiUrl}/instructor/courses/${courseId}/assign-student`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ userId })
    });
    if (response.ok) {
      showNotification("Öğrenci kursa atandı");
      fetchAdminData(); // Güncelle
    } else {
      showNotification("Atama başarısız", "error");
    }
  } catch (error) {
    showNotification("Atama sırasında hata oluştu", "error");
  }
};
const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Bu kursu tamamen silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!")) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/admin/courses/${courseId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        // Silinen kursu listeden anlık olarak çıkartalım
        setCourses(prev => prev.filter(course => course._id !== courseId));
        // Üst istatistik barını güncellemek için admin verilerini tazeleyelim
        fetchAdminData();
        showNotification("Kurs başarıyla silindi");
      } else {
        const data = await response.json();
        showNotification(data.error || "Kurs silinirken hata oluştu", "error");
      }
    } catch (error) {
      console.error("Kurs silinirken hata:", error);
      showNotification("Kurs silinirken hata oluştu", "error");
    }
  };
  // ==================== RENDER FUNCTIONS ====================

 const renderUsersTab = () => (
  <div style={styles.card}>
    <div style={styles.cardHeader}>
      <h2 style={styles.cardTitle}>Kullanıcılar ({users.length})</h2>
    </div>
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>İsim</th>
            <th style={styles.tableHeader}>E-posta</th>
            <th style={styles.tableHeader}>Kayıt Tarihi</th>
            <th style={styles.tableHeader}>Rol</th>
          
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user._id}>
              <td style={styles.tableCell}>{user.name}</td>
              <td style={styles.tableCell}>{user.email}</td>
              <td style={{...styles.tableCell, ...styles.dateCell}}>
                {user.createdAt ? formatDate(user.createdAt) : "Kayıt tarihi yok"}
              </td>
              <td style={styles.tableCell}>
                <select
                  value={user.role}
                  onChange={e => handleUserRoleChange(user._id, e.target.value)}
                  style={styles.formSelect}
                  disabled={user.email === "admin@example.com"}
                >
                  <option value="student">Öğrenci</option>
                  <option value="instructor">Eğitmen</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

 const renderCoursesTab = () => (
  <div style={styles.card}>
    <div style={styles.cardHeader}>
      <h2 style={styles.cardTitle}>Kurslar ({courses.length})</h2>
    </div>
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>Kurs Adı</th>
            <th style={styles.tableHeader}>Eğitmen</th>
            <th style={styles.tableHeader}>Kayıtlı Öğrenci</th>
            <th style={styles.tableHeader}>İşlemler</th> {/* Yeni Sütun Başlığı */}
          </tr>
        </thead>
        <tbody>
          {courses.map((course) => (
            <tr key={course._id}>
              <td style={styles.tableCell}>{course.title}</td>
              <td style={styles.tableCell}>{course.instructor}</td>
              <td style={styles.tableCell}>
                <span style={styles.enrollmentBadge}>
                  {course.enrollmentCount ?? (course.enrollments ? course.enrollments.length : 0)} öğrenci
                </span>
              </td>
              <td style={styles.tableCell}>
                <button
                  onClick={() => handleDeleteCourse(course._id)}
                  style={styles.deleteButton}
                >
                  Sil
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
  const renderContactsTab = () => (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>İletişim Formları ({contactStats.total})</h2>
      </div>
      
      {/* Filtreler */}
      <div style={styles.filterContainer}>
        <input
          type="text"
          placeholder="İsim, e-posta veya konu ara..."
          value={contactFilters.search}
          onChange={(e) => setContactFilters(prev => ({ ...prev, search: e.target.value }))}
          style={styles.filterInput}
        />
        <select
          value={contactFilters.status}
          onChange={(e) => setContactFilters(prev => ({ ...prev, status: e.target.value }))}
          style={styles.filterSelect}
        >
          <option value="">Tüm Durumlar</option>
          <option value="pending">Bekleyen</option>
          <option value="reviewed">İncelendi</option>
          <option value="contacted">İletişim Kuruldu</option>
          <option value="processed">Tamamlandı</option>
          <option value="rejected">Reddedildi</option>
        </select>
      </div>

      {/* İstatistik Kartları */}
      <div style={styles.statsGrid}>
        <div style={styles.statCardPrimary}>
          <h3 style={styles.statNumber}>{contactStats.total}</h3>
          <p style={styles.statLabel}>Toplam Form</p>
        </div>
        <div style={styles.statCardWarning}>
          <h3 style={styles.statNumber}>{contactStats.byStatus.pending}</h3>
          <p style={styles.statLabel}>Bekleyen</p>
        </div>
        <div style={styles.statCardInfo}>
          <h3 style={styles.statNumber}>{contactStats.byStatus.reviewed}</h3>
          <p style={styles.statLabel}>İncelenen</p>
        </div>
        <div style={styles.statCardSuccess}>
          <h3 style={styles.statNumber}>{contactStats.byStatus.processed}</h3>
          <p style={styles.statLabel}>Tamamlanan</p>
        </div>
      </div>

      {/* Contact Listesi */}
      <div style={styles.tableContainer}>
        {contactLoading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p>Formlar yükleniyor...</p>
          </div>
        ) : contacts.length === 0 ? (
          <div style={styles.emptyContainer}>
            <p>Gösterilecek form bulunamadı</p>
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Tarih</th>
                  <th style={styles.tableHeader}>Ad Soyad</th>
                  <th style={styles.tableHeader}>E-posta</th>
                  <th style={styles.tableHeader}>Birim</th>
                  <th style={styles.tableHeader}>Durum</th>
                  <th style={styles.tableHeader}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact._id}>
                    <td style={{ ...styles.tableCell, ...styles.dateCell }}>
                      {formatDate(contact.submittedAt || contact.createdAt || contact.date)}
                    </td>
                    <td style={styles.tableCell}>
                      {contact.name}
                    </td>
                    <td style={styles.tableCell}>
                      {contact.email}
                    </td>
                    <td style={styles.tableCell}>
                      {contact.subject}
                    </td>
                    <td style={styles.tableCell}>
                      <select
                        value={contact.status}
                        onChange={(e) => handleContactStatusChange(contact._id, e.target.value)}
                        style={styles.statusSelect(contact.status)}
                        disabled={contactLoading}
                      >
                        <option value="pending">Bekleyen</option>
                        <option value="reviewed">İncelendi</option>
                        <option value="contacted">İletişim Kuruldu</option>
                        <option value="processed">Tamamlandı</option>
                        <option value="rejected">Reddedildi</option>
                      </select>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionsCell}>
                        <button
                          onClick={() => handleViewContactDetails(contact._id)}
                          style={styles.detailButton}
                        >
                          Detay
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact._id)}
                          style={styles.deleteButton}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Sayfalama */}
            {contactPagination.totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setContactPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={contactPagination.page === 1}
                  style={styles.pageButton(false)}
                >
                  ◀️ Önceki
                </button>
                <span>
                  Sayfa {contactPagination.page} / {contactPagination.totalPages}
                </span>
                <button
                  onClick={() => setContactPagination(prev => ({ 
                    ...prev, 
                    page: Math.min(contactPagination.totalPages, prev.page + 1) 
                  }))}
                  disabled={contactPagination.page === contactPagination.totalPages}
                  style={styles.pageButton(false)}
                >
                  Sonraki ▶️
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
const handleCreateCourse = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${apiUrl}/admin/courses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(newCourse)
    });
    if (response.ok) {
      showNotification("Kurs başarıyla eklendi");
      setNewCourse({
        title: "",
        description: "",
        instructor: "",
        instructorEmail: "",
        duration: "",
        level: "Beginner",
        price: ""
      });
      fetchAdminData(); // Kurs listesini güncelle
    } else {
      showNotification("Kurs eklenemedi", "error");
    }
  } catch (error) {
    showNotification("Kurs eklenirken hata oluştu", "error");
  } finally {
    setLoading(false);
  }
};
  const renderCreateCourseTab = () => (
  <div style={styles.card}>
    <div style={styles.cardHeader}>
      <h2 style={styles.cardTitle}>Yeni Kurs Ekle</h2>
    </div>
    <form style={styles.formContainer} onSubmit={handleCreateCourse}>
  <div style={styles.formGroup}>
    <label style={styles.formLabel}>Kurs Adı</label>
    <input
      type="text"
      style={styles.formInput}
      value={newCourse.title}
      onChange={e => setNewCourse(prev => ({ ...prev, title: e.target.value }))}
      required
    />
  </div>
  <div style={styles.formGroup}>
    <label style={styles.formLabel}>Açıklama</label>
    <textarea
      style={styles.formTextarea}
      value={newCourse.description}
      onChange={e => setNewCourse(prev => ({ ...prev, description: e.target.value }))}
      required
    />
  </div>
  <div style={styles.formGrid}>
    <div style={styles.formGroup}>
      <label style={styles.formLabel}>Eğitmen Adı</label>
      <input
        type="text"
        style={styles.formInput}
        value={newCourse.instructor}
        onChange={e => setNewCourse(prev => ({ ...prev, instructor: e.target.value }))}
        required
      />
    </div>
    <div style={styles.formGroup}>
      <label style={styles.formLabel}>Eğitmen E-posta</label>
      <input
        type="email"
        style={styles.formInput}
        value={newCourse.instructorEmail}
        onChange={e => setNewCourse(prev => ({ ...prev, instructorEmail: e.target.value }))}
        required
      />
    </div>
  </div>
  <div style={styles.formGrid}>
    
    <div style={styles.formGroup}>
      <label style={styles.formLabel}>Seviye</label>
      <select
        style={styles.formSelect}
        value={newCourse.level}
        onChange={e => setNewCourse(prev => ({ ...prev, level: e.target.value }))}
        required
      >
        <option value="Beginner">Başlangıç</option>
        <option value="Intermediate">Orta</option>
        <option value="Advanced">İleri</option>
      </select>
    </div>
    
  </div>
  <div style={styles.formActions}>
    <button style={styles.createButton} type="submit" disabled={loading}>
      <Plus size={18} className="inline-icon" /> Kurs Oluştur
    </button>
  </div>
</form>
  </div>
);

  const renderContactModal = () => (
    <div style={styles.modalOverlay} onClick={() => setShowContactModal(false)}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Form Detayları</h3>
          <button 
            onClick={() => setShowContactModal(false)}
            style={styles.closeButton}
          >
            ✕
          </button>
        </div>

        {selectedContact && (
          <>
            <div style={styles.contactGrid}>
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Ad Soyad</div>
                <div style={styles.contactValue}>{selectedContact.name}</div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>E-posta</div>
                <div style={styles.contactValue}>{selectedContact.email}</div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Telefon</div>
                <div style={styles.contactValue}>{selectedContact.phone || '-'}</div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Okul Bilgisi</div>
                <div style={styles.contactValue}>{selectedContact.schoolInfo}</div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Doğum Tarihi</div>
                <div style={styles.contactValue}>
                  {selectedContact.birthDate} 
                  {selectedContact.birthDate && ` (${calculateAge(selectedContact.birthDate)} yaş)`}
                </div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Birim</div>
                <div style={styles.contactValue}>{selectedContact.subject}</div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>İletişim Tercihi</div>
                <div style={styles.contactValue}>
                  {selectedContact.contactMethod === 'email' ? 'E-posta' :
                   selectedContact.contactMethod === 'phone' ? 'Telefon' :
                   selectedContact.contactMethod === 'both' ? 'Her İkisi' : selectedContact.contactMethod}
                </div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Bültene Abone</div>
                <div style={styles.contactValue}>
                  {selectedContact.subscribe ? 'Evet' : 'Hayır'}
                </div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Gönderim Tarihi</div>
                <div style={styles.contactValue}>{formatDate(selectedContact.submittedAt)}</div>
              </div>
              
              <div style={styles.contactItem}>
                <div style={styles.contactLabel}>Durum</div>
                <div style={styles.contactValue}>
                  <select
                    value={selectedContact.status}
                    onChange={(e) => handleContactStatusChange(selectedContact._id, e.target.value)}
                    style={styles.statusSelect(selectedContact.status)}
                  >
                    <option value="pending">Bekleyen</option>
                    <option value="reviewed">İncelendi</option>
                    <option value="contacted">İletişim Kuruldu</option>
                    <option value="processed">Tamamlandı</option>
                    <option value="rejected">Reddedildi</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.contactItem}>
              <div style={styles.contactLabel}>Mesaj</div>
              <div style={styles.messageBox}>
                {selectedContact.message}
              </div>
            </div>

            <div style={styles.contactItem}>
              <div style={styles.contactLabel}>Admin Notları</div>
              <textarea
                value={selectedContact.adminNotes || ''}
                onChange={(e) => handleUpdateAdminNotes(selectedContact._id, e.target.value)}
                placeholder="Bu form için notlarınızı buraya yazın..."
                style={styles.notesTextarea}
              />
            </div>
          </>
        )}
        
        <div style={styles.modalActions}>
          <button
            onClick={() => setShowContactModal(false)}
            style={styles.closeModalButton}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <style>{globalStyles}</style>
      
      {notification.show && (
        <div style={styles.notification(notification.type)}>
          {notification.message}
        </div>
      )}
      
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Admin Paneli</h1>
        <div style={styles.headerStats}>
          <span>Toplam Kullanıcı: {stats.totalUsers}</span>
          <span>Toplam Kurs: {stats.totalCourses}</span>
          <span>Formlar: {contactStats.total}</span>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div style={styles.tabs}>
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              style={styles.tabButton(isActive)}
              onClick={() => setActiveTab(tab.id)}
            >
              <span style={{ fontSize: "16px" }}>{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <>
          <div style={styles.dashboardGrid}>
            <div style={styles.statCard}> 
              <h3 style={styles.statNumber}>{stats.totalUsers}</h3>
              <p style={styles.statLabel}>Toplam Kullanıcı</p>
            </div>
            <div style={styles.statCard}> 
              <h3 style={styles.statNumber}>{stats.totalCourses}</h3>
              <p style={styles.statLabel}>Toplam Kurs</p>
            </div>
            <div style={styles.statCard}> 
              <h3 style={styles.statNumber}>{stats.totalEnrollments}</h3>
              <p style={styles.statLabel}>Toplam Kayıt</p>
            </div>
            <div style={styles.statCard}> 
              <h3 style={styles.statNumber}>{instructors.length}</h3>
              <p style={styles.statLabel}>Toplam Eğitmen</p>
            </div>
            <div style={styles.statCard}> 
              <h3 style={styles.statNumber}>{contactStats.total}</h3>
              <p style={styles.statLabel}>Toplam Form</p>
            </div>
            <div style={styles.statCard}> 
              <h3 style={styles.statNumber}>{contactStats.byStatus.pending}</h3>
              <p style={styles.statLabel}>Bekleyen Form</p>
            </div>
          </div>
          
          {/* Grafik Alanı */}
          <div style={styles.chartContainer}>
            <h3 style={styles.chartTitle}>İstatistikler Grafiği</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[
                { name: "Kullanıcılar", value: stats.totalUsers },
                { name: "Kurslar", value: stats.totalCourses },
                { name: "Kayıtlar", value: stats.totalEnrollments },
                { name: "Eğitmenler", value: instructors.length },
                { name: "Formlar", value: contactStats.total }
              ]}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis allowDecimals={false} stroke="#666" />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#2d8131c3" radius={[8,8,0,0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Users Tab */}
      {activeTab === "users" && renderUsersTab()}

      {/* Courses Tab */}
      {activeTab === "courses" && renderCoursesTab()}

      {/* Contacts Tab */}
      {activeTab === "contacts" && renderContactsTab()}

      {/* Appointments Tab */}
      {activeTab === "appointments" && <AdminAppointmentPanel />}

      {/* Inventory Tab */}
      {activeTab === "inventory" && <InventoryManagement />}

      {/* Create Course Tab */}
      {activeTab === "create-course" && renderCreateCourseTab()}

      {/* Contact Detail Modal */}
      {showContactModal && renderContactModal()}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#f7f7f7',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '32px',
    paddingBottom: '20px',
    borderBottom: '1px solid #eee',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 8px 0',
  },
  headerStats: {
    display: 'flex',
    gap: '20px',
    color: '#666',
    fontSize: '14px',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '32px',
    overflowX: 'auto',
    paddingBottom: '4px',
  },
  tabButton: (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: isActive ? '#288756eb' : '#fff',
    color: isActive ? 'white' : '#333',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    ':hover': {
      background: isActive ? '#42a947' : '#f8f9fa',
    }
  }),
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #eee',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '24px',
    borderBottom: '1px solid #eee',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    margin: '0',
  },
  tableContainer: {
    padding: '0 24px 24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    padding: '16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#333',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '14px',
  },
  tableCell: {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
    color: '#333',
  },
  dateCell: {
    color: '#666',
    fontSize: '13px',
  },
  statusBadge: (status) => ({
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: status === "active" ? '#d4edda' : '#f8d7da',
    color: status === "active" ? '#2a6939ff' : '#721c24',
    fontSize: '12px',
    fontWeight: '500',
  }),
  levelBadge: (level) => ({
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: 
      level === "Beginner" ? '#d1ecf1' :
      level === "Intermediate" ? '#fff3cd' : '#d4edda',
    color: 
      level === "Beginner" ? '#0c5460' :
      level === "Intermediate" ? '#856404' : '#155724',
    fontSize: '12px',
    fontWeight: '500',
  }),
  enrollmentBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#2e7232ff',
    color: 'white',
    fontSize: '12px',
    fontWeight: '500',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  statCard: {
    backgroundColor: '#a8a85bff',
    color: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    textAlign: 'center',
    transition: 'transform 0.2s ease',
    cursor: 'pointer',
    ':hover': {
      transform: 'translateY(-2px)',
    }
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: 'bold',
    margin: '8px 0',
  },
  statLabel: {
    fontSize: '14px',
    opacity: 0.9,
    margin: '0',
  },
  chartContainer: {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #eee',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    marginBottom: '32px',
    padding: '24px',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
  },
  filterContainer: {
    padding: '0 24px',
    marginBottom: '20px',
    display: 'flex',
    gap: '12px',
  },
  filterInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: '#f8f9fa',
  },
  filterSelect: {
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: '#f8f9fa',
    minWidth: '150px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    padding: '0 24px',
    marginBottom: '24px',
  },
  statCardPrimary: {
    backgroundColor: '#2a842eff',
    color: 'white',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
  },
  statCardWarning: {
    backgroundColor: '#ed6c02',
    color: 'white',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
  },
  statCardInfo: {
    backgroundColor: '#0288d1',
    color: 'white',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
  },
  statCardSuccess: {
    backgroundColor: '#2e7d32',
    color: 'white',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '40px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #42a947',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  emptyContainer: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  statusSelect: (status) => {
    const statusColors = {
      pending: { background: '#fff3cd', color: '#856404', border: '#ffeaa7' },
      reviewed: { background: '#d1ecf1', color: '#0c5460', border: '#bee5eb' },
      contacted: { background: '#cce5ff', color: '#004085', border: '#b8daff' },
      processed: { background: '#d4edda', color: '#155724', border: '#c3e6cb' },
      rejected: { background: '#f8d7da', color: '#721c24', border: '#f5c6cb' }
    };
    const color = statusColors[status] || { background: '#f8f9fa', color: '#333', border: '#e0e0e0' };
    
    return {
      padding: '6px 12px',
      borderRadius: '6px',
      border: `1px solid ${color.border}`,
      backgroundColor: color.background,
      color: color.color,
      fontSize: '12px',
      cursor: 'pointer',
      minWidth: '120px',
      fontWeight: '500',
    };
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
  },
  detailButton: {
    padding: '6px 12px',
    background: 'transparent',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  deleteButton: {
    padding: '6px 12px',
    background: '#dc3545',
    color: 'white',
    border: '1px solid #dc3545',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '20px',
    padding: '20px 0',
  },
  pageButton: (isActive) => ({
    padding: '8px 16px',
    border: '1px solid #e0e0e0',
    background: isActive ? '#1a8a20ff' : '#fff',
    color: isActive ? 'white' : '#333',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    ':hover': {
      background: '#f8f9fa',
    }
  }),
  formContainer: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#333',
    fontSize: '14px',
  },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: '#f8f9fa',
    ':focus': {
      outline: 'none',
      borderColor: '#42a947',
    }
  },
  formTextarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: '#f8f9fa',
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit',
    ':focus': {
      outline: 'none',
      borderColor: '#42a947',
    }
  },
  formSelect: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: '#f8f9fa',
    cursor: 'pointer',
    ':focus': {
      outline: 'none',
      borderColor: '#42a947',
    }
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  formGrid3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '20px',
  },
  formActions: {
    marginTop: '30px',
    textAlign: 'right',
  },
  createButton: {
    padding: '12px 30px',
    background: '#42a947',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    border: '1px solid #eee',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #eee',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    margin: '0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#f8f9fa',
    }
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  contactItem: {
    marginBottom: '16px',
  },
  contactLabel: {
    fontWeight: '500',
    color: '#666',
    marginBottom: '4px',
    fontSize: '14px',
  },
  contactValue: {
    color: '#333',
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
    fontSize: '14px',
  },
  messageBox: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '8px',
    minHeight: '100px',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.5',
    fontSize: '14px',
    color: '#333',
  },
  notesTextarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '8px',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    background: '#f8f9fa',
    ':focus': {
      outline: 'none',
      borderColor: '#42a947',
    }
  },
  modalActions: {
    marginTop: '24px',
    textAlign: 'right',
  },
  closeModalButton: {
    padding: '10px 20px',
    background: 'transparent',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  notification: (type) => {
    const colors = {
      success: { background: '#d4edda', color: '#155724', border: '#c3e6cb' },
      error: { background: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
      warning: { background: '#fff3cd', color: '#856404', border: '#ffeaa7' },
      info: { background: '#d1ecf1', color: '#0c5460', border: '#bee5eb' }
    };
    const color = colors[type] || colors.info;
    
    return {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '16px 20px',
      backgroundColor: color.background,
      color: color.color,
      border: `1px solid ${color.border}`,
      borderRadius: '8px',
      zIndex: 1001,
      animation: 'slideIn 0.3s ease',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      minWidth: '300px',
      fontSize: '14px',
    };
  },
};

export default AdminPage;